import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { Button, CheckIcon, ChevronDownIcon, PlusIcon, cx } from "@wato/ui";
import type { WorkspaceSummary } from "../../api/types";
import { popoverPlacementClass, type PopoverPlacement } from "../workspace/popover-placement";
import { rememberWorkspaceId } from "./last-workspace";

export interface WorkspaceSwitcherProps {
  readonly currentId: string;
  readonly currentName: string;
  readonly workspaces: readonly WorkspaceSummary[];
  /** OWN-320: icon rail — shows the first letter instead of the name. */
  readonly compact?: boolean;
  readonly placement?: PopoverPlacement;
}

/**
 * Header dropdown listing the user's workspaces (WP-111 build step 9). Choosing one navigates
 * to `/w/:id`: every later API call carries that id in its path, so switching needs neither a
 * re-login nor a new token (the JWT never holds a workspace, ADR-0013). The choice is
 * remembered in this browser for the next visit.
 */
export function WorkspaceSwitcher({
  currentId,
  currentName,
  workspaces,
  compact = false,
  placement = "end-down",
}: WorkspaceSwitcherProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    menuRef.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus();
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
  }, [open]);

  function close() {
    setOpen(false);
    buttonRef.current?.focus();
  }

  function choose(id: string) {
    rememberWorkspaceId(id);
    setOpen(false);
    if (id !== currentId) void navigate(`/w/${id}`);
  }

  function onMenuKey(event: KeyboardEvent<HTMLDivElement>) {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [],
    );
    const index = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      items[(index + 1) % items.length]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      items[(index - 1 + items.length) % items.length]?.focus();
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  }

  const itemClass =
    "flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-fg hover:bg-surface-sunken focus-visible:outline-2 focus-visible:outline-focus";

  return (
    <div ref={rootRef} className="relative min-w-0">
      <Button
        ref={buttonRef}
        variant="secondary"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`${t("dashboard.switcher.label")}: ${currentName}`}
        onClick={() => setOpen((o) => !o)}
        className={compact ? "size-9 px-0!" : "max-w-60 min-w-0 max-md:max-w-28"}
        endIcon={compact ? undefined : <ChevronDownIcon className="size-4 text-fg-muted" />}
      >
        <span className="block truncate">
          {compact ? (
            <>
              <span aria-hidden="true">{[...currentName][0] ?? "?"}</span>
              <span className="sr-only">{currentName}</span>
            </>
          ) : (
            currentName
          )}
        </span>
      </Button>
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={t("dashboard.switcher.list")}
          onKeyDown={onMenuKey}
          className={`absolute z-20 flex w-72 flex-col gap-1 rounded-lg border border-line bg-surface p-2 shadow-md ${popoverPlacementClass(placement)}`}
        >
          {workspaces.map((ws) => {
            const current = ws.id === currentId;
            return (
              <button
                key={ws.id}
                type="button"
                role="menuitemradio"
                aria-checked={current}
                tabIndex={-1}
                onClick={() => choose(ws.id)}
                className={cx(itemClass, current && "font-semibold")}
              >
                <span className="flex size-4 shrink-0 items-center">
                  {current && <CheckIcon className="size-4 text-on-action-ghost" />}
                </span>
                <span className="min-w-0 flex-1 truncate">{ws.name}</span>
                {ws.isOwner && (
                  <span className="shrink-0 text-xs text-fg-muted">
                    {t("dashboard.switcher.owner")}
                  </span>
                )}
              </button>
            );
          })}
          <div className="my-1 border-t border-line" role="separator" />
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            onClick={() => {
              setOpen(false);
              void navigate("/workspaces/new");
            }}
            className={itemClass}
          >
            <PlusIcon className="size-4 text-fg-muted" />
            <span>{t("dashboard.switcher.create")}</span>
          </button>
        </div>
      )}
    </div>
  );
}
